import React from 'react';
import DataGrid, { Column, Scrolling, LoadPanel, HeaderFilter, Paging, Selection } from 'devextreme-react/data-grid';
import { DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH, DEFAULT_PAGE_SIZE } from 'components/common/DataGridDevEx/DataGrid.constants';
import HyperLinkRenderer from 'components/common/DataGridDevEx/CustomRenderers/HyperLinkRenderer';
import { GRID_HEIGHT, SUB_TITLE_CONTENT } from './TransmitInternalPODialog.constants';
import { TransmitInternalPODialogStyles } from './TransmitInternalPODialog.styles';
import { WithStyles, withStyles } from '@material-ui/core';
import { ISubTitleProperties, ITransmitInternalPOGridProperties } from './TransmitInternalPODialog.properties';
import MoreIcon from '@markinson/uicomponents-v2/SvgIcons/More';
import { withRouter, RouteComponentProps } from 'react-router-dom';

type OwnProps = ITransmitInternalPOGridProperties;
type StyleProps = WithStyles<typeof TransmitInternalPODialogStyles>;
type Props = OwnProps & StyleProps & RouteComponentProps;

const DRILL_DOWN_WIDTH = 50;

const TransmitInternalPOGrid = ({ classes, loading, data, onSelectedRowChanged }: Props) => {

    const [isFirstTime, setIsFirstTime] = React.useState(true);
    const dataGridRef = React.useRef<DataGrid>();

    React.useEffect(() => setIsFirstTime(true), []);

    React.useEffect(
        () => {
            if (dataGridRef && dataGridRef.current) {
                if (loading) {
                    dataGridRef.current.instance.beginCustomLoading('');
                } else {
                    dataGridRef.current.instance.endCustomLoading();
                }
            }
        },
        [loading]
    );

    const SelectAndFocusFirstRow = () => {
        setTimeout(
            () => {
                dataGridRef.current?.instance?.focus();
                dataGridRef?.current.instance.selectRowsByIndexes([0]);
            },
            0
        );
    };

    const onContentReady = React.useCallback(
        (e) => {
            if (isFirstTime && e.component.getDataSource()?.items().length) {
                SelectAndFocusFirstRow();
                setIsFirstTime(false);
            }
        },
        [isFirstTime]
    );

    const handleOnSelectionChanged = ({ selectedRowsData }) => {
        onSelectedRowChanged((selectedRowsData && selectedRowsData[0]) ? selectedRowsData[0].PurchaseOrder : {});
    };

    const detailCellComponent = React.useCallback(
        (allProps) => {
            const url = allProps?.data?.data?.PurchaseOrderHyperlink;

            return <MoreIcon onClick={() => window.open(url)} />;
        },
        []
    );

    return (
        <div className={classes.Container}>
            <DataGrid
                ref={dataGridRef}
                id='TransmitInternalPO'
                dataSource={data}
                keyExpr={'PurchaseOrder'}
                noDataText=''
                repaintChangesOnly={true}
                remoteOperations={true}
                allowColumnReordering={true}
                columnResizingMode={'nextColumn'}
                allowColumnResizing={true}
                renderAsync={true}
                showBorders={false}
                hoverStateEnabled={true}
                onContentReady={onContentReady}
                onSelectionChanged={handleOnSelectionChanged}
                height={GRID_HEIGHT}
            >
                <HeaderFilter visible />
                <Column
                    allowEditing={false}
                    allowResizing={false}
                    fixed={true}
                    alignment='center'
                    allowHeaderFiltering={false}
                    cellComponent={detailCellComponent}
                    width={DRILL_DOWN_WIDTH}
                />
                <Column
                    dataField={'PurchaseOrder'}
                    caption={'Purchase order'}
                    allowEditing={false}
                    allowHeaderFiltering={false}
                    cellRender={({ value }) => <HyperLinkRenderer
                        colDef={{ headerName: value }}
                        value={{ PurchaseOrder: value, FieldValue: value }}
                        link={'purchase-order-enquiry/order-details'}
                    />
                    }
                />
                <Column
                    caption={'Supplier account'}
                    dataField={'Supplier'}
                    allowFiltering={false}
                />
                <Column
                    caption={'Supplier name'}
                    dataField={'SupplierName'}
                    allowFiltering={false}
                />
                <Column
                    caption={'Warehouse'}
                    dataField={'Warehouse'}
                    allowFiltering={false}
                />
                <Column
                    caption={'P/O status'}
                    dataField={'StatusDescription'}
                    allowFiltering={false}
                />
                <Paging defaultPageSize={DEFAULT_PAGE_SIZE} defaultPageIndex={0} />
                <Scrolling mode={'infinite'} />
                <Selection mode={'single'} />
                <LoadPanel shading={false} height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH} text={''} showPane={false} />
            </DataGrid>
        </div>
    );
};

export default withRouter(withStyles(TransmitInternalPODialogStyles)(React.memo(TransmitInternalPOGrid)));

export const SubTitle = withStyles(TransmitInternalPODialogStyles)(({ classes }: ISubTitleProperties) => (<span className={classes.subTitle}>{SUB_TITLE_CONTENT}</span>));
